import { Dispatch, SetStateAction, useEffect, useState } from "react";
import clsx from "clsx";
import {
  BriefcaseIcon,
  ClockIcon,
  ExternalLinkIcon,
  MailIcon,
  PhoneIcon,
  TagsIcon,
  XIcon,
} from "lucide-react";

import {
  CopyButton,
  JobPipelineCandidateSheetCandidacyCommentsAndActivity,
  JobPipelineCandidateSheetCandidacyInterestButtons,
  JobPipelineCandidateSheetCandidacyScoreDropdown,
  JobPipelineCandidateSheetExecutiveSummary,
  JobPipelineCandidateSheetResumeContainer,
  JobPipelineCandidateSheetScreeningAnswers,
  JobPipelineCandidateSheetStatusDropdown,
  SheetCertifications,
  SheetEducations,
  SheetEmptyState,
  SheetEndorsements,
  SheetHeader,
  SheetIndustries,
  SheetNavigation,
  SheetProfessionalSummary,
  SheetSkills,
  SheetWorkExperiences,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useScrollToSheetTopOnSelectedIndexChange } from "@/hooks";
import { useGetJob } from "@/hooks/queries";
import { CandidacyWithProfileResponse } from "@/types";

enum Tab {
  Profile = "Profile",
  Resume = "Resume",
  Screening = "Screening",
}

// TODO: Improve this...
const ExternalJobLink = () => {
  const { data: job, status } = useGetJob();

  if (status === "pending") {
    return null;
  } else if (status === "error") {
    return null;
  }

  return (
    <Button
      className="h-auto p-0 text-primary-900 hover:bg-transparent hover:text-primary-900"
      onClick={() => {
        window.open(
          `${window.location.protocol}//${window.location.host}/companies/${job.companyId}/jobs/${job.id}`,
          "_blank",
        );
      }}
      variant="ghost"
    >
      {job.title}
      <ExternalLinkIcon className="h-4 w-4" />
    </Button>
  );
};

export const JobPipelineCandidateSheet = ({
  candidaciesWithProfiles,
  selectedCandidacyIndex,
  setSelectedCandidacyIndex,
}: {
  candidaciesWithProfiles: CandidacyWithProfileResponse[];
  selectedCandidacyIndex: number | null;
  setSelectedCandidacyIndex: Dispatch<SetStateAction<number | null>>;
}) => {
  const [activeTab, setActiveTab] = useState(Tab.Profile);
  const sheetContentRef = useScrollToSheetTopOnSelectedIndexChange(
    selectedCandidacyIndex,
  );

  // TODO: See if this is the best way to handle this.
  // This ensures that the tabs go back to the default state when the selected candidacy changes.
  useEffect(() => {
    setActiveTab(Tab.Profile);
  }, [selectedCandidacyIndex]);

  // TODO: Is there a better way to handle this?
  if (selectedCandidacyIndex === null) return null;

  const selectedCandidate = candidaciesWithProfiles[selectedCandidacyIndex];
  const hasScreeningAnswers = !!selectedCandidate.candidacy.screeningAnswers;
  const isProfileEmpty =
    !selectedCandidate.candidateProfile.professionalSummary &&
    !selectedCandidate.candidateProfile.workExperience.length &&
    !selectedCandidate.candidateProfile.skills.length &&
    !selectedCandidate.candidateProfile.industries.length &&
    !selectedCandidate.candidateProfile.certifications.length &&
    !selectedCandidate.candidateProfile.education.length &&
    !selectedCandidate.candidateProfile.endorsementsReceived.length;

  return (
    <Sheet
      onOpenChange={() => {
        setSelectedCandidacyIndex(null);
        // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2187447622
        setTimeout(() => {
          const body = document.querySelector("body");
          if (body) {
            body.style.pointerEvents = "";
          }
        }, 100);
      }}
      open
    >
      <SheetContent
        // TODO: Better width handling?
        className="flex w-[70%] !min-w-[1000px] !max-w-[70%] flex-col gap-0 overflow-y-auto p-0"
        ref={sheetContentRef}
        hideClose
      >
        <div className="flex items-center justify-between px-6 py-4">
          <SheetClose
            // Prevents the close button from being focused when navigating with the keyboard.
            tabIndex={-1}
          >
            <XIcon className="h-4 w-4 text-slate-700" />
          </SheetClose>
          <div className="flex items-center gap-2">
            <JobPipelineCandidateSheetCandidacyInterestButtons
              candidacy={selectedCandidate.candidacy}
              isInterested={selectedCandidate.candidacyInterest}
            />
            <JobPipelineCandidateSheetStatusDropdown
              candidacy={selectedCandidate.candidacy}
              setSelectedCandidacyIndex={setSelectedCandidacyIndex}
            />
            <SheetNavigation
              currentIndex={selectedCandidacyIndex}
              length={candidaciesWithProfiles.length}
              setIndex={setSelectedCandidacyIndex}
            />
          </div>
        </div>
        <div className="flex flex-1 border-t border-slate-200">
          <div className="w-full border-r border-slate-200 p-6">
            {/* Header Section */}
            <div className="flex flex-col items-start gap-4 pb-6">
              <div className="relative w-full">
                <SheetHeader
                  profile={selectedCandidate.candidateProfile.profile}
                />
                <div className="absolute right-0 top-0">
                  <JobPipelineCandidateSheetCandidacyScoreDropdown
                    candidacyId={selectedCandidate.candidacy.id}
                    candidacyScore={selectedCandidate.candidacyScore}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-6">
                  <div className="flex w-[180px] items-center gap-2">
                    <MailIcon className="h-4 w-4 text-slate-700" />
                    <p className="text-sm text-slate-700">Email address</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <p className="text-sm text-slate-700">
                      {selectedCandidate.candidateProfile.email}
                    </p>
                    <CopyButton
                      toCopy={selectedCandidate.candidateProfile.email}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex w-[180px] items-center gap-2">
                    <PhoneIcon className="h-4 w-4 text-slate-700" />
                    <p className="text-sm text-slate-700">Phone number</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <p className="text-sm text-slate-700">
                      {selectedCandidate.candidateProfile.profile.phone}
                    </p>
                    <CopyButton
                      toCopy={selectedCandidate.candidateProfile.profile.phone}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex w-[180px] items-center gap-2">
                    <BriefcaseIcon className="h-4 w-4 text-slate-700" />
                    <p className="text-sm text-slate-700">Job</p>
                  </div>
                  <ExternalJobLink />
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex w-[180px] items-center gap-2">
                    <TagsIcon className="h-4 w-4 text-slate-700" />
                    <p className="text-sm text-slate-700">Tags</p>
                  </div>
                  {selectedCandidate.candidateProfile.profile.yoeRange && (
                    <Badge className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      {
                        selectedCandidate.candidateProfile.profile.yoeRange
                          .range
                      }{" "}
                      years of experience
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <Tabs
              // This is important to prevent the tabs from being controlled by keyboard shortcuts.
              activationMode="manual"
              defaultValue={Tab.Profile}
              onValueChange={(value: string) => {
                setActiveTab(value as Tab);
              }}
              value={activeTab}
            >
              <TabsList>
                <TabsTrigger className="w-[75px]" value={Tab.Profile}>
                  Profile
                </TabsTrigger>
                {selectedCandidate.candidateProfile.resumeFile?.url && (
                  <TabsTrigger className="w-[75px]" value={Tab.Resume}>
                    Resume
                  </TabsTrigger>
                )}
                {hasScreeningAnswers && (
                  <TabsTrigger className="w-[165px]" value={Tab.Screening}>
                    Screening Questions
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent className="mt-6" value={Tab.Profile}>
                {/* Executive Summary */}
                <JobPipelineCandidateSheetExecutiveSummary
                  summary={selectedCandidate.candidacy.summary}
                />

                {isProfileEmpty ? (
                  <div className="flex items-center justify-center pt-28">
                    <SheetEmptyState
                      setResumeTab={
                        selectedCandidate.candidateProfile.resumeFile?.url
                          ? () => {
                              setActiveTab(Tab.Resume);
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : (
                  <>
                    {/* Professional Summary */}
                    <SheetProfessionalSummary
                      containerClasses={clsx(
                        "border-none",
                        // TODO: Improve this...
                        !selectedCandidate.candidacy.summary && "pt-0",
                      )}
                      professionalSummary={
                        selectedCandidate.candidateProfile.professionalSummary
                      }
                    />

                    {/* Work Experience  */}
                    <SheetWorkExperiences
                      workExperiences={
                        selectedCandidate.candidateProfile.workExperience
                      }
                    />

                    {/* Skills  */}
                    <SheetSkills
                      skills={selectedCandidate.candidateProfile.skills}
                    />

                    {/* Industries */}
                    <SheetIndustries
                      industries={selectedCandidate.candidateProfile.industries}
                    />

                    {/* Certifications / Licenses  */}
                    <SheetCertifications
                      certifications={
                        selectedCandidate.candidateProfile.certifications
                      }
                    />

                    {/* Education */}
                    <SheetEducations
                      educations={selectedCandidate.candidateProfile.education}
                    />

                    {/* Education */}
                    <SheetEndorsements
                      endorsements={
                        selectedCandidate.candidateProfile.endorsementsReceived
                      }
                    />
                  </>
                )}
              </TabsContent>
              {selectedCandidate.candidateProfile.resumeFile?.url && (
                <TabsContent className="mt-6" value={Tab.Resume}>
                  <JobPipelineCandidateSheetResumeContainer
                    href={selectedCandidate.candidateProfile.resumeFile.url}
                  />
                </TabsContent>
              )}
              {hasScreeningAnswers && (
                <TabsContent className="mt-6" value={Tab.Screening}>
                  <JobPipelineCandidateSheetScreeningAnswers
                    screeningAnswers={
                      selectedCandidate.candidacy.screeningAnswers
                    }
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>
          {/* TODO: Better handling for this width. */}
          <div className="w-[316px] shrink-0 py-6">
            <JobPipelineCandidateSheetCandidacyCommentsAndActivity
              candidacyId={selectedCandidate.candidacy.id}
              // TODO: Better way to handle first and/or last name missing?
              candidateFirstName={
                selectedCandidate.candidateProfile.profile.firstName ?? ""
              }
              candidateLastName={
                selectedCandidate.candidateProfile.profile.lastName ?? ""
              }
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
